import { useState, useEffect } from "react";
import { useLocation } from "@/lib/react-router-dom-shim";
import { supabase } from "@/integrations/supabase/client";
import { Camera, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface PhotoCredit {
  id: string;
  photographer_name: string | null;
  photographer_url: string | null;
  source: string;
  location_in_site: string;
}

export const FooterPhotoCredits = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [credits, setCredits] = useState<PhotoCredit[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreditsForCurrentPage();
  }, [location.pathname]);

  const loadCreditsForCurrentPage = async () => {
    try {
      const currentPath = location.pathname;
      const searchTerms: string[] = [];
      
      if (currentPath === "/" || currentPath === "") {
        searchTerms.push("Página inicial");
      }
      if (currentPath === "/resorts") {
        searchTerms.push("/resorts");
        searchTerms.push("Featured Destinations");
      }
      if (currentPath.startsWith("/resorts/")) {
        const region = currentPath.replace("/resorts/", "");
        searchTerms.push(`/resorts/${region}`);
        searchTerms.push(`Resorts ${region.charAt(0).toUpperCase() + region.slice(1)}`);
      }

      if (searchTerms.length === 0) {
        setCredits([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("static_images_audit")
        .select("id, photographer_name, photographer_url, source, location_in_site")
        .not("photographer_name", "is", null);

      if (error) throw error;

      const matchingCredits = (data || []).filter(image => {
        return searchTerms.some(term => 
          image.location_in_site.toLowerCase().includes(term.toLowerCase())
        );
      });

      const uniqueCredits = matchingCredits.reduce((acc: PhotoCredit[], current) => {
        const exists = acc.find(item => item.photographer_name === current.photographer_name);
        if (!exists && current.photographer_name) {
          acc.push(current);
        }
        return acc;
      }, []);

      setCredits(uniqueCredits);
    } catch (error) {
      console.error("Error loading photo credits:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || credits.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-primary-foreground/50 hover:text-primary-foreground/80 transition-colors">
        <Camera className="h-3 w-3" />
        <span>{t('footer.photoCredits', 'Créditos de fotos')}</span>
        {isOpen ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-primary-foreground/50">
          {credits.map((credit) => (
            <span key={credit.id} className="inline-flex items-center gap-1">
              <span>{credit.source}:</span>
              {credit.photographer_url ? (
                <a
                  href={credit.photographer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-colors inline-flex items-center gap-0.5 underline underline-offset-2"
                >
                  {credit.photographer_name}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              ) : (
                <span className="text-primary-foreground/70">{credit.photographer_name}</span>
              )}
            </span>
          ))}
        </div>
        <p className="mt-1.5 text-[10px] text-primary-foreground/40">
          {t('footer.unsplashLicense', 'Imágenes bajo licencia Unsplash - uso gratuito con atribución apreciada')}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
};